import React, { useState, useRef, useEffect, useCallback } from "react";
import classNames from "classnames/bind";
import styles from "./CustomSelect.module.scss";

const cx = classNames.bind(styles);

export interface SelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const handleToggle = useCallback(() => {
    if (!disabled) setOpen((prev) => !prev);
  }, [disabled]);

  const handleSelect = useCallback(
    (val: string) => {
      onChange(val);
      setOpen(false);
    },
    [onChange]
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <div
      ref={ref}
      className={cx("selectWrapper", className, { open, disabled })}
    >
      <button
        type="button"
        className={cx("trigger")}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={cx("triggerLabel", { placeholder: !selected })}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={cx("arrow")}
          width="12"
          height="12"
          viewBox="0 0 12 12"
        >
          <path d="M6 8L1 3h10z" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div className={cx("dropdown")}>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={cx("option", { selected: opt.value === value })}
              onClick={() => handleSelect(opt.value)}
              title={opt.label}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
